// Imported first so page modules can override the shared primitives.
import ui from './ui.module.css';
import type { FunctionReturnType } from 'convex/server';
import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { AvatarControls } from '../lib/AvatarControls';
import { avatarTint } from '../lib/avatarTint';
import { presetOf } from '../lib/AvatarPicker';
import { api, useMutation } from '../lib/convex';
import { canPromptInstall, isIos, isStandalone, promptInstall } from '../lib/install';
import { MemberAvatar } from '../lib/MemberAvatar';
import { pushPermission } from '../lib/push';
import { storeBoundDevice } from '../lib/boundDevice';
import { findDeviceTokenKey, getDeviceToken } from '../lib/tokens';
import { initial, safeAppPath, tint } from './format';
import { errorCode, useCanPromptInstall, useLauncherPage, useSafeQuery } from './hooks';
import { BellIcon, ChevronLeftIcon, ChevronRightIcon, LogoMark } from './icons';
import { IosInstallSteps } from './InstallHelp';
import styles from './Join.module.css';
import { requestPush, syncPushSubscription } from './pushSync';
import { setLastCircle } from './storage';

type Lookup = FunctionReturnType<typeof api.invites.lookup>;
type Member = Lookup['members'][number];
type Bound = { slug: string; token: string };
type Step =
  | { kind: 'start' }
  | { kind: 'pick' }
  | { kind: 'pin'; member: Member }
  | ({ kind: 'picture'; member: Member } & Bound)
  | ({ kind: 'install' } & Bound)
  | ({ kind: 'push' } & Bound);

const PIN_LENGTH = 4;

/** Route "/join/:inviteCode" — which one are you → PIN → install → push → home. */
export function Join() {
  useLauncherPage();
  const { inviteCode = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = safeAppPath(params.get('next'));

  const lookup = useSafeQuery(api.invites.lookup, inviteCode ? { inviteCode } : 'skip');
  const publicKey = useSafeQuery(api.push.publicKey, {});
  const bind = useMutation(api.invites.bind);

  const [step, setStep] = useState<Step>(params.get('switch') ? { kind: 'pick' } : { kind: 'start' });
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [shake, setShake] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const pinRef = useRef<HTMLInputElement>(null);

  const locked = lockedUntil !== null && now < lockedUntil;

  useEffect(() => {
    if (lockedUntil === null) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= lockedUntil) {
        setLockedUntil(null);
        setPinError(null);
        pinRef.current?.focus();
      }
    }, 250);
    return () => clearInterval(id);
  }, [lockedUntil]);

  function finish(slug: string) {
    setLastCircle(slug);
    if (next) window.location.assign(next);
    else navigate('/', { replace: true });
  }

  async function afterInstall({ slug, token }: Bound) {
    const key = publicKey.data;
    const permission = pushPermission();
    if (key && permission === 'granted') {
      try {
        await syncPushSubscription(key, [token]);
      } catch (err) {
        console.warn('Circles: push subscription failed', err);
      }
    } else if (key && permission === 'default') {
      setStep({ kind: 'push', slug, token });
      return;
    }
    finish(slug);
  }

  function afterBind(bound: Bound) {
    if (!isStandalone() && (isIos() || canPromptInstall())) setStep({ kind: 'install', ...bound });
    else void afterInstall(bound);
  }

  function pickMember(member: Member) {
    // Focus inside the tap so iOS opens the numeric keyboard.
    pinRef.current?.focus();
    setPin('');
    setPinError(null);
    setStep({ kind: 'pin', member });
  }

  async function submitPin(value: string) {
    if (step.kind !== 'pin' || busy) return;
    const { member } = step;
    setBusy(true);
    try {
      const res = await bind({ inviteCode, memberId: member.id, pin: value, userAgent: navigator.userAgent });
      if (res.ok) {
        pinRef.current?.blur();
        const bound = { slug: storeBoundDevice(res), token: res.deviceToken };
        // A member already set up on another device has been through this; switching back skips it.
        if (member.bound) afterBind(bound);
        else setStep({ kind: 'picture', member, ...bound });
        return;
      }
      setPin('');
      setShake((n) => n + 1);
      if (res.reason === 'wrong_pin') {
        setPinError(`That's not ${member.name}'s PIN. Try again.`);
      } else if (res.reason === 'locked') {
        pinRef.current?.blur();
        setNow(Date.now());
        setLockedUntil(Date.now() + (res.retryInMs ?? 60_000));
        setPinError(null);
      } else {
        setPinError(null);
        setStep({ kind: 'pick' });
      }
    } catch {
      setPin('');
      setPinError("Couldn't reach Bolt Circles. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  function onPinChange(e: ChangeEvent<HTMLInputElement>) {
    if (step.kind !== 'pin' || busy || locked) return;
    const value = e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH);
    setPin(value);
    if (value.length > 0) setPinError(null);
    if (value.length === PIN_LENGTH) void submitPin(value);
  }

  // Kept mounted across steps so pickMember() can focus it during the tap.
  const pinInput = (
    <input
      ref={pinRef}
      id="pin"
      name="pin"
      className={styles.pinInput}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="off"
      maxLength={PIN_LENGTH}
      value={pin}
      onChange={onPinChange}
      readOnly={busy}
      disabled={locked}
      tabIndex={step.kind === 'pin' ? 0 : -1}
      aria-label="4-digit PIN"
    />
  );

  let body: ReactNode;
  if (!inviteCode || errorCode(lookup.error) === 'NOT_FOUND') {
    body = (
      <Message
        title="This invite link doesn't work"
        text="It may have been mistyped, or the circle was removed. Ask whoever sent it for a fresh link."
      />
    );
  } else if (lookup.error) {
    body = <Message title="Something went wrong" text="We couldn't open this invite. Try again in a moment." />;
  } else if (!lookup.data) {
    body = <PickSkeleton />;
  } else {
    const data = lookup.data;
    const existingKey = findDeviceTokenKey(data.circle.id);
    const existing = existingKey ? getDeviceToken(existingKey) : null;
    if (step.kind === 'start' && existing) {
      body = (
        <div className={ui.center}>
          <p className={ui.eyebrow}>{data.circle.name}</p>
          <h1 className={ui.title}>You're already in</h1>
          <p className={ui.sub}>
            This device is set up as <strong>{existing.memberName}</strong>.
          </p>
          <div className={ui.actions}>
            <button className={ui.primary} onClick={() => finish(existingKey ?? data.circle.publicId)}>
              Continue as {existing.memberName}
            </button>
            <button className={ui.secondary} onClick={() => setStep({ kind: 'pick' })}>
              Switch member
            </button>
          </div>
        </div>
      );
    } else if (step.kind === 'start' || step.kind === 'pick') {
      body = <PickMember lookup={data} onPick={pickMember} />;
    } else if (step.kind === 'pin') {
      const secondsLeft = locked && lockedUntil ? Math.ceil((lockedUntil - now) / 1000) : 0;
      body = (
        <div className={styles.pinStep}>
          <button className={styles.back} onClick={() => setStep({ kind: 'pick' })}>
            <ChevronLeftIcon size={20} /> Not {step.member.name}?
          </button>
          <div className={styles.pinHead}>
            <span className={`${ui.avatar} ${styles.person} ${styles.bigAvatar}`} style={{ background: tint(step.member.id, 1.35) }}>
              {initial(step.member.name)}
            </span>
            <h1 className={ui.title}>Hi {step.member.name}</h1>
            <p className={ui.sub}>Enter your 4-digit PIN.</p>
          </div>
          <label htmlFor="pin" key={shake} className={`${styles.cells} ${shake > 0 ? styles.shake : ''} ${locked ? styles.cellsLocked : ''}`}>
            {Array.from({ length: PIN_LENGTH }, (_, i) => (
              <span
                key={i}
                className={`${styles.cell} ${i < pin.length ? styles.filled : ''} ${i === pin.length && !busy && !locked ? styles.current : ''}`}
              />
            ))}
          </label>
          <p className={styles.pinMessage} role="status" aria-live="polite">
            {locked ? (
              <span className={styles.error}>Too many tries. Try again in {secondsLeft}s.</span>
            ) : pinError ? (
              <span className={styles.error}>{pinError}</span>
            ) : busy ? (
              'Checking…'
            ) : (
              <span className={styles.muted}>Forgot it? Ask whoever runs {data.circle.name}.</span>
            )}
          </p>
        </div>
      );
    } else if (step.kind === 'picture') {
      body = <PictureStep circleName={data.circle.name} member={step.member} token={step.token} onDone={() => afterBind({ slug: step.slug, token: step.token })} />;
    } else if (step.kind === 'install') {
      body = <InstallStep circleName={data.circle.name} onDone={() => void afterInstall(step)} />;
    } else {
      body = (
        <PushStep
          circleName={data.circle.name}
          onEnable={async () => {
            const key = publicKey.data;
            if (key) await requestPush(key, [step.token]);
            finish(step.slug);
          }}
          onSkip={() => finish(step.slug)}
        />
      );
    }
  }

  return (
    <main className={ui.page}>
      {pinInput}
      {body}
    </main>
  );
}

function PickMember({ lookup, onPick }: { lookup: Lookup; onPick: (m: Member) => void }) {
  return (
    <div>
      <div className={styles.brand}>
        <LogoMark size={36} />
      </div>
      <p className={ui.eyebrow}>Joining {lookup.circle.name}</p>
      <h1 className={ui.title}>Which one are you?</h1>
      {lookup.members.length === 0 ? (
        <p className={ui.sub}>Nobody has been added to this circle yet.</p>
      ) : (
        <ul className={styles.members}>
          {lookup.members.map((m) => (
            <li key={m.id}>
              <button className={`${styles.member} ${m.bound ? styles.bound : ''}`} onClick={() => onPick(m)}>
                <span
                  className={`${ui.avatar} ${m.bound ? styles.mutedAvatar : styles.person}`}
                  style={m.bound ? undefined : { background: tint(m.id, 1.35) }}
                >
                  {initial(m.name)}
                </span>
                <span className={styles.memberText}>
                  <span className={styles.memberName}>{m.name}</span>
                  {m.bound ? (
                    <span className={styles.memberHint}>Already set up on a device. You can add this one too.</span>
                  ) : m.role ? (
                    <span className={styles.memberHint}>{m.role}</span>
                  ) : null}
                </span>
                <ChevronRightIcon className={styles.chevron} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Right after the PIN: pick an illustrated picture or a photo, or skip and keep the automatic animal. */
function PictureStep({ circleName, member, token, onDone }: { circleName: string; member: Member; token: string; onDone: () => void }) {
  const home = useSafeQuery(api.launcher.home, { deviceToken: token });
  const uploadUrl = useMutation(api.members.myAvatarUploadUrl);
  const setAvatar = useMutation(api.members.setMyAvatar);
  const setAvatarPreset = useMutation(api.members.setMyAvatarPreset);
  const setAvatarHue = useMutation(api.members.setMyAvatarHue);
  const removeAvatar = useMutation(api.members.removeMyAvatar);
  const me = home.data?.me;
  const background = me ? avatarTint(me.avatarHue) : tint(member.id, 1.35);
  return (
    <div className={styles.pictureStep}>
      <p className={`${ui.eyebrow} ${styles.okLine}`}>You're in</p>
      <h1 className={ui.title}>Pick your picture</h1>
      <p className={ui.sub}>It's how {circleName} sees you. You can change it any time in Settings.</p>
      <div className={styles.pictureControls}>
        {me ? (
          <AvatarControls
            name={me.name}
            avatarKind={me.avatarKind}
            preset={presetOf(me.avatarUrl, me.avatarKind)}
            hue={me.avatarHue}
            pickerOpen
            uploadLabel="Use a photo"
            preview={<MemberAvatar name={me.name} avatarUrl={me.avatarUrl} className={`${ui.avatar} ${styles.person} ${styles.pictureAvatar}`} style={{ background }} />}
            uploader={{
              getUploadUrl: () => uploadUrl({ deviceToken: token }),
              setAvatar: (storageId) => setAvatar({ deviceToken: token, storageId }),
            }}
            setPreset={(preset) => setAvatarPreset({ deviceToken: token, preset })}
            setHue={(hue) => setAvatarHue({ deviceToken: token, hue })}
            remove={() => removeAvatar({ deviceToken: token })}
            buttonClassName={styles.pictureButton}
            dangerClassName={`${styles.pictureButton} ${styles.pictureRemove}`}
          />
        ) : (
          <div className={ui.skeleton} style={{ height: 240 }} aria-busy="true" />
        )}
      </div>
      <div className={`${ui.actions} ${styles.pictureActions}`}>
        <button className={ui.primary} onClick={onDone} disabled={!me || me.avatarKind === 'auto'}>
          Done
        </button>
        {(!me || me.avatarKind === 'auto') && (
          <button className={ui.ghost} onClick={onDone}>
            Skip
          </button>
        )}
      </div>
    </div>
  );
}

function InstallStep({ circleName, onDone }: { circleName: string; onDone: () => void }) {
  const canPrompt = useCanPromptInstall();
  const ios = isIos();
  return (
    <div className={ui.center}>
      <div className={styles.stepMark}>
        <LogoMark size={64} />
      </div>
      <p className={`${ui.eyebrow} ${styles.okLine}`}>You're in</p>
      <h1 className={ui.title}>Put Bolt Circles on your Home Screen</h1>
      <p className={ui.sub}>Everything {circleName} sends you lives there, and that's how notifications reach you.</p>
      {ios && <IosInstallSteps />}
      <div className={ui.actions}>
        {!ios && canPrompt ? (
          <button
            className={ui.primary}
            onClick={() => {
              void promptInstall().then(onDone);
            }}
          >
            Install Bolt Circles
          </button>
        ) : ios ? (
          <button className={ui.primary} onClick={onDone}>
            Done
          </button>
        ) : null}
        <button className={ui.ghost} onClick={onDone}>
          Not now
        </button>
      </div>
    </div>
  );
}

function PushStep({ circleName, onEnable, onSkip }: { circleName: string; onEnable: () => Promise<void>; onSkip: () => void }) {
  const [pending, setPending] = useState(false);
  return (
    <div className={ui.center}>
      <div className={`${styles.stepMark} ${styles.bellMark}`} aria-hidden>
        <BellIcon size={32} />
      </div>
      <p className={ui.eyebrow}>One more thing</p>
      <h1 className={ui.title}>Want a heads-up?</h1>
      <p className={ui.sub}>Get a notification when someone in {circleName} sends you something, or an app reminds you.</p>
      <div className={ui.actions}>
        <button
          className={ui.primary}
          disabled={pending}
          onClick={() => {
            setPending(true);
            void onEnable().finally(() => setPending(false));
          }}
        >
          Turn on notifications
        </button>
        <button className={ui.ghost} onClick={onSkip} disabled={pending}>
          Not now
        </button>
      </div>
    </div>
  );
}

function Message({ title, text }: { title: string; text: string }) {
  return (
    <div className={ui.center}>
      <div className={styles.brand}>
        <LogoMark size={36} />
      </div>
      <h1 className={ui.title}>{title}</h1>
      <p className={ui.sub}>{text}</p>
      <div className={ui.actions}>
        <a className={ui.secondary} href="/">
          Go to Bolt Circles
        </a>
      </div>
    </div>
  );
}

function PickSkeleton() {
  return (
    <div aria-busy="true">
      <div className={styles.brand}>
        <LogoMark size={36} />
      </div>
      <div className={ui.skeleton} style={{ height: 14, width: 120, marginBottom: 12 }} />
      <div className={ui.skeleton} style={{ height: 34, width: 260 }} />
      <div className={styles.members}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={ui.skeleton} style={{ height: 72 }} />
        ))}
      </div>
    </div>
  );
}
